import { useCallback } from "react";
import { useAppDispatch } from "../store/hooks";
import { UIActions } from "../store/ui-slice";
import { getFreshToken } from "../store/auth-actions";
import { DATABASE_URL } from "../store/events-actions";
import { t } from "../i18n";

const useHttp = () => {
  const dispatch = useAppDispatch();

  const sendRequest = useCallback(
    async (
      requestConfig: {
        url?: string;
        // A database path, e.g. /tasks/<uid>. Used instead of url so the auth
        // token is read at the moment of sending, not when the screen rendered.
        dbPath?: string;
        method?: string;
        headers?: HeadersInit;
        body?: any;
        hideAfterRequest?: boolean;
        successMessage?: string;
      },
      applyDataFn: ((data: any) => void) | null = null
    ) => {
      dispatch(
        UIActions.loading({
          message: t.loading,
        })
      );
      try {
        const send = async (forceNewToken = false) => {
          const url = requestConfig.dbPath
            ? DATABASE_URL +
              requestConfig.dbPath +
              ".json?auth=" +
              (await dispatch(getFreshToken(forceNewToken)))
            : (requestConfig.url as string);
          return fetch(url, {
            method: requestConfig.method ? requestConfig.method : "GET",
            headers: requestConfig.headers ? requestConfig.headers : {},
            body: requestConfig.body ? JSON.stringify(requestConfig.body) : null,
          });
        };

        let response = await send();
        // Another tab may have renewed the session, or the token may have been
        // revoked early - either way one fresh token settles it.
        if (response.status === 401 && requestConfig.dbPath) {
          response = await send(true);
        }

        if (response.ok) {
          const data = await response.json();
          if(requestConfig.hideAfterRequest) {
            dispatch(UIActions.hide());
          }
          if (requestConfig.successMessage) {
            dispatch(UIActions.success({ message: requestConfig.successMessage }));
          }
          if(applyDataFn) {
            applyDataFn(data);
          }
        } else {
          const data = await response.json();

          const errorText =
            t.authErrors[data.error?.message] ?? t.somethingWentWrong;

          dispatch(
            UIActions.error({
              message: errorText,
            })
          );
        }
      } catch (err: any) {
        dispatch(
          UIActions.error({
            // fetch reports a lost connection as a TypeError in browser wording
            message:
              err instanceof TypeError
                ? t.noConnection
                : err.message || t.somethingWentWrong,
          })
        );
      }
    },
    [dispatch]
  );

  return {
    sendRequest,
  };
};

export default useHttp;
